import { useMemo } from 'react';
import { useParams } from 'react-router';
import { canViewTask, filterVisibleTasksForUser, isExternalPartnerUser } from '../utils/projectAccess';

type UseOrganizationDataOptions = {
  includeAllOrganizations?: boolean;
  includeProjectId?: string;
};

const normalizeIdentity = (value: unknown): string => String(value || '').trim().toLowerCase();

export function useOrganizationData(state: any, options: UseOrganizationDataOptions = {}) {
  const includeAllOrganizations = options.includeAllOrganizations === true;
  const params = useParams<{ projectId?: string }>();
  const scopedProjectId = normalizeIdentity(options.includeProjectId || params.projectId);
  const organizations = state?.organizations ?? [];
  const currentOrgId = state?.currentOrganization?.id || state?.currentUser?.organizationId;
  const currentUser = state?.currentUser;

  const currentUserIdentitySet = useMemo(() => {
    const identities = new Set<string>();
    const userId = normalizeIdentity(currentUser?.id);
    const userEmail = normalizeIdentity(currentUser?.email);

    if (userId) {
      identities.add(userId);
    }
    if (userEmail) {
      identities.add(userEmail);
    }

    return identities;
  }, [currentUser?.id, currentUser?.email]);

  const currentUserProjectIdSet = useMemo(() => {
    const projectIds = new Set<string>();
    const directProjects = Array.isArray(currentUser?.projects) ? currentUser.projects : [];
    directProjects.forEach((projectId: unknown) => {
      const normalized = normalizeIdentity(projectId);
      if (normalized) {
        projectIds.add(normalized);
      }
    });

    const orgEntries = Array.isArray(currentUser?.organizations) ? currentUser.organizations : [];
    orgEntries.forEach((entry: unknown) => {
      if (!entry || typeof entry !== 'object') {
        return;
      }

      const projects = (entry as any).projects;
      const projectIdsFromEntry = (entry as any).projectIds;
      const nestedProjects = Array.isArray(projects) ? projects : Array.isArray(projectIdsFromEntry) ? projectIdsFromEntry : [];

      nestedProjects.forEach((projectId: unknown) => {
        const normalized = normalizeIdentity(projectId);
        if (normalized) {
          projectIds.add(normalized);
        }
      });
    });

    return projectIds;
  }, [currentUser?.projects, currentUser?.organizations]);

  const currentOrganization = useMemo(() => {
    if (!currentOrgId) {
      return null;
    }

    return organizations.find((org: any) => org.id === currentOrgId) ?? null;
  }, [organizations, currentOrgId]);
  /* -------------------------------------------------- */
  /* PROJECT IDS FROM ORGANIZATIONS                     */
  /* -------------------------------------------------- */

  const projectIdSet = useMemo(() => {
    return new Set((currentOrganization?.projects ?? []) as string[]);
  }, [currentOrganization]);

  /* -------------------------------------------------- */
  /* PROJECTS                                           */
  /* -------------------------------------------------- */
  // console.log("[Hooks/useOrganizationData]  projects: ", state?.projects);
  // console.log("[Hooks/useOrganizationData]  organizations: ", state?.organizations);
  // console.log("[Hooks/useOrganizationData]  projectIdSet: ", projectIdSet);
  const projects = useMemo(() => {
    const allProjects = state?.projects ?? [];
    const allProjectsById = new Map(
      allProjects.map((project: any) => [normalizeIdentity(project.id), project])
    );

    const scopedProjects = (() => {
      if (includeAllOrganizations || !currentOrgId) {
        return allProjects;
      }

      return allProjects.filter((project: any) => {
        const normalizedProjectId = normalizeIdentity(project.id);
        const matchesOrgField =
          project.organizationId === currentOrgId ||
          (project.organizationIds || []).includes(currentOrgId);

        // Keep compatibility with legacy organization.projects arrays when present.
        const matchesLegacyProjectList = projectIdSet.size > 0 ? projectIdSet.has(project.id) : true;
        const matchesScopedProject = !!scopedProjectId && normalizedProjectId === scopedProjectId;

        const matchesTeamMember = (project.teamMembers || []).some((member: any) =>
          currentUserIdentitySet.has(normalizeIdentity(member?.userId))
        );
        const matchesAllowedUser = (project.allowedUserIds || []).some((allowedUserId: unknown) =>
          currentUserIdentitySet.has(normalizeIdentity(allowedUserId))
        );
        const matchesUserProjectList = currentUserProjectIdSet.has(normalizedProjectId);
        const matchesUserAccess = matchesTeamMember || matchesAllowedUser || matchesUserProjectList;

        return (matchesOrgField && matchesLegacyProjectList) || matchesScopedProject || matchesUserAccess;
      });
    })();

    if (!isExternalPartnerUser(currentUser)) {
      return scopedProjects;
    }

    const visibleProjectIds = new Set(
      (state?.tasks ?? [])
        .filter((task: any) => {
          const taskProject = allProjectsById.get(normalizeIdentity(task.projectId));
          return canViewTask(currentUser, task, taskProject);
        })
        .map((task: any) => normalizeIdentity(task.projectId))
        .filter(Boolean)
    );

    return scopedProjects.filter((project: any) =>
      visibleProjectIds.has(normalizeIdentity(project.id))
    );
  }, [
    state?.projects,
    state?.tasks,
    currentOrgId,
    projectIdSet,
    includeAllOrganizations,
    scopedProjectId,
    currentUser,
    currentUserIdentitySet,
    currentUserProjectIdSet,
  ]);

  /* -------------------------------------------------- */
  /* PROJECT IDS                                        */
  /* -------------------------------------------------- */

  const filteredProjectIdSet = useMemo(() => {
    return new Set(projects.map((p: any) => p.id));
  }, [projects]);

  /* -------------------------------------------------- */
  /* TASKS                                              */
  /* -------------------------------------------------- */

  const tasks = useMemo(() => {
    const scopedTasks = (state?.tasks ?? []).filter((t: any) =>
      filteredProjectIdSet.has(t.projectId)
    );

    const scopedProjectsById = new Map(
      projects.map((project: any) => [normalizeIdentity(project.id), project])
    );

    return filterVisibleTasksForUser(
      state?.currentUser,
      scopedTasks,
      (task) => scopedProjectsById.get(normalizeIdentity(task.projectId)) || null
    );
  }, [state?.tasks, filteredProjectIdSet, state?.currentUser, projects]);

  /* -------------------------------------------------- */
  /* SPRINTS                                            */
  /* -------------------------------------------------- */

  const sprints = useMemo(() => {
    return (state?.sprints ?? []).filter((s: any) =>
      filteredProjectIdSet.has(s.projectId)
    );
  }, [state?.sprints, filteredProjectIdSet]);

  const epics = useMemo(() => {
    return (state?.epics ?? []).filter((e: any) => {
      if (!filteredProjectIdSet.has(e.projectId)) {
        return false;
      }

      if (!scopedProjectId) {
        return true;
      }

      return normalizeIdentity(e.projectId) === scopedProjectId;
    });
  }, [state?.epics, filteredProjectIdSet, scopedProjectId]);

  const users = useMemo(() => {
    const allUsers = state?.users ?? [];

    if (includeAllOrganizations || !currentOrgId || !!scopedProjectId) {
      return allUsers;
    }

    return allUsers.filter((user: any) => user.organizationId === currentOrgId);
  }, [state?.users, currentOrgId, includeAllOrganizations, scopedProjectId]);
  // console.info("[Hooks] projects: ", projects);
  // console.info("[Hooks] tasks: ", tasks);
  // console.info("[Hooks] sprint: ", sprints);
  // console.info("[Hooks] users: ", users);

  const orgId = useMemo(() => {
    if (!scopedProjectId) {
      return currentOrgId;
    }

    const scopedProject = (state?.projects ?? []).find(
      (project: any) => normalizeIdentity(project?.id) === scopedProjectId
    );

    return scopedProject?.organizationId || currentOrgId;
  }, [scopedProjectId, state?.projects, currentOrgId]);

  return {
    projects,
    tasks,
    sprints,
    users,
    epics,
    projectIdSet,
    orgId
  };
}
